<template>
	<v-dialog
		v-model="dialog"
		max-width="800"
	>
		<template v-slot:activator="{ on }">
			<v-btn
				color="red darken-1"
				dark
				v-on="on"
			>
				Add Alarm
			</v-btn>
		</template>

		<v-card>
			<v-list-item-content class="pa-8">
				<v-list-item-title class="headline">Add Alarm</v-list-item-title>
				<v-container>
					<v-row>
						<v-col>
							<v-autocomplete
								v-model="exchangeAutoCompleteFormItem"
								:items="this.exchangeNames"
								:loading="isLoadingExchanges"
								:rules="[rules.required]"
								cache-items
								@focus="loadExchanges"
								color="white"
								item-text="Name"
								item-value="Exchange"
								label="Exchanges"
								placeholder="Search the different exchange names"
								prepend-icon="mdi-database-search"
								return-object
								autocomplete="off"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-autocomplete
								v-if="exchangeAutoCompleteFormItem.length !== 0"
								v-model="stocksAutoCompleteFormItem"
								:items="this.stockNames"
								:loading="isLoadingStocks"
								:rules="[rules.required]"
								@focus="loadStocks"
								color="white"
								item-text="Name"
								item-value="Stocks"
								label="Stocks"
								placeholder="Search the different stocks names"
								prepend-icon="mdi-database-search"
								return-object
								autocomplete="off"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-radio-group v-model="buySellRadioFormItem" row label="Trigger alarm when I should ">
								<v-radio label="Buy Stocks " value="buy"/>
								<v-radio label="Sell Stocks" value="sell"/>
							</v-radio-group>
						</v-col>
					</v-row>
					<v-row>
						<v-col md="6">
							<v-text-field
								v-model="desiredDeltaFormItem"
								:rules="[rules.required, rules.deltaMin]"
								label="How much would you like it to change in %?"
								type="number"
								step="1"
								outlined
							/>
						</v-col>
						<v-col md="6">
							<v-text-field
								v-model="refreshPeriodDays"
								:rules="[rules.isValidRefreshPeriodInput]"
								label="Refresh every X, Y, Z days. Empty for never."
								hint="1,5,15,30"
								type="text"
								step="1"
								outlined
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col md="6">
							<v-checkbox
								v-model="useCurrentPriceCheckbox"
								label="Use current stock price as baseline"
								color="red"
								style="margin-top: 0"
								hide-details
							/>
						</v-col>

					</v-row>
					<v-row>
						<v-col md=6 v-if="!useCurrentPriceCheckbox">
							<v-text-field
								v-model="buyPriceFormItem"
								:rules="[rules.required, rules.priceMin]"
								label="Baseline price to use"
								type="number"
								outlined
							/>
						</v-col>
					</v-row>
				</v-container>

			</v-list-item-content>
			<v-card-actions>
				<v-spacer/>

				<v-btn
					color="red darken-1"
					text
					@click="dialog = false"
				>
					Close
				</v-btn>

				<v-btn
					color="green darken-1"
					text
					@click="saveAlarmClicked"
				>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import {mapGetters} from "vuex";
import {ADD_ALARM, FETCH_EXCHANGES, FETCH_STOCKS} from "@/store/actions.type";
import {Intention} from "@/common/intentions.type";

export default {
	name: "AddAlarmDialog",
	data() {
		return {
			"dialog": "",
			"buySellRadioFormItem": "buy",
			"exchangeAutoCompleteFormItem": "",
			"isLoadingExchanges": false,
			"exchangesLoaded": false,
			"stocksAutoCompleteFormItem": "",
			"isLoadingStocks": false,
			"stocksLoaded": false,
			"previousExchangeCode": null,
			"desiredDeltaFormItem": 0,
			"refreshPeriodDays": "",
			"useCurrentPriceCheckbox": false,
			"buyPriceFormItem": null,
			rules: {
				required: value => !!value || 'Required.',
				deltaMin: value => value >= 0,
				priceMin: value => value >= 0,
				isValidRefreshPeriodInput: value => this.isValidRefreshPeriodInput(value.split(",")),
			},
		};
	},
	computed: {
		...mapGetters(["exchanges", "stocks"]),
		exchangeNames() {
			return this.exchanges.map(e => `[${e.exchange_code}] ${e.exchange_name}`)
		},
		getExchange() {
			return this.exchanges.find(
				e => `[${e.exchange_code}] ${e.exchange_name}` === this.exchangeAutoCompleteFormItem
			)
		},
		getStock() {
			return this.stocks.find(
				s => `[${s.symbol}] ${s.description}` === this.stocksAutoCompleteFormItem
			)
		},
		stockNames() {
			return this.stocks.map(s => `[${s.symbol}] ${s.description}`)
		},
		isBuy() {
			return this.buySellRadioFormItem === Intention.buy();
		},
		isSell() {
			return !this.isBuy;
		},
	},
	methods: {
		loadExchanges() {
			if (this.exchanges.length !== 0) {
				this.isLoadingExchanges = false;
				return;
			}

			if (this.isLoadingExchanges === true) {
				return;
			}

			this.isLoadingExchanges = true;
			this.$store.dispatch(FETCH_EXCHANGES)
		},
		loadStocks() {
			if (this.stocks !== null || this.stocks !== []) {
				this.isLoadingStocks = false;
			}

			if (this.isLoadingStocks === true) {
				return;
			}

			if (this.getExchange.exchange_code === this.previousExchangeCode) {
				return;
			}

			this.stocksAutoCompleteFormItem = "";
			this.isLoadingStocks = true;
			this.$store.dispatch(FETCH_STOCKS, this.getExchange.exchange_id);
			this.previousExchangeCode = this.getExchange.exchange_code;
		},
		saveAlarmClicked() {
			this.dialog = false;

			let addAlarmRequest = {
				stock_id: parseInt(this.getStock.id),
				intention_id: Intention.fromIntentionString(this.buySellRadioFormItem),
				desired_delta: parseInt(this.desiredDeltaFormItem),
			};

			let refreshDays = [];
			let days = this.refreshPeriodDays.split(",");
			for (let i = 0; i < days.length; i++) {
				let day = parseInt(days[i]);
				if (day > 0) {
					refreshDays.push(day);
				}
			}

			if (refreshDays.length > 0) {
				addAlarmRequest.refresh_period_days = refreshDays;
			}

			if (this.useCurrentPriceCheckbox === false) {
				addAlarmRequest.base_price = {
					amount: this.buyPriceFormItem.toString(),
					currency: this.getStock.currentPrice.currency
				};
			}

			this.$store.dispatch(ADD_ALARM, addAlarmRequest);
		},
		isValidRefreshPeriodInput(arrayValues) {
			if (!(arrayValues instanceof Array)) {
				return false;
			}

			for (let day in arrayValues) {
				if (!Number.isInteger(parseInt(day))) {
					return false;
				}
			}

			return true;
		}
	},
}
</script>

<style scoped>

</style>